import {
  collection,
  doc,
  query,
  where,
  serverTimestamp,
  setDoc,
  updateDoc,
  getDoc,
  getDocs,
  waitForPendingWrites,
} from "firebase/firestore";
import { db } from "../firebase";

/**
 * Phase 4 — availability sharing data layer (MVP).
 *
 * Per-friend, whole-calendar redacted share. The owner grants a viewer
 * permission to see their cached busy windows + free-time marks via the
 * getSharedAvailabilityProjection Cloud Function (the sole projection path).
 *
 * Schema is minimal so future fields (questingGroupIds, calendarIds, scope
 * toggles) can be added additively without migration.
 */

export const AVAILABILITY_SHARE_STATUSES = Object.freeze(["active", "revoked"]);

export function availabilityShareIdFor(ownerId, viewerId) {
  if (!ownerId || !viewerId) {
    throw new Error("availabilityShareIdFor requires ownerId and viewerId.");
  }
  return `${ownerId}_${viewerId}`;
}

export const availabilitySharesRef = () =>
  collection(db, "availabilityShares");

export const availabilityShareRef = (ownerId, viewerId) =>
  doc(db, "availabilityShares", availabilityShareIdFor(ownerId, viewerId));

export const outgoingActiveAvailabilitySharesQuery = (ownerId) =>
  query(
    availabilitySharesRef(),
    where("ownerId", "==", ownerId),
    where("status", "==", "active"),
  );

export const incomingActiveAvailabilitySharesQuery = (viewerId) =>
  query(
    availabilitySharesRef(),
    where("viewerId", "==", viewerId),
    where("status", "==", "active"),
  );

export async function grantAvailabilityShare({
  ownerId,
  viewerId,
  redactMarkTitles = false,
}) {
  if (!ownerId) throw new Error("ownerId is required.");
  if (!viewerId) throw new Error("viewerId is required.");
  if (ownerId === viewerId) {
    throw new Error("Cannot share availability with yourself.");
  }

  const ref = availabilityShareRef(ownerId, viewerId);
  const existing = await getDoc(ref);

  const now = serverTimestamp();
  const base = {
    ownerId,
    viewerId,
    redactMarkTitles: Boolean(redactMarkTitles),
    status: "active",
    updatedAt: now,
    revokedAt: null,
  };

  if (existing.exists()) {
    await updateDoc(ref, base);
  } else {
    await setDoc(ref, { ...base, createdAt: now });
  }
  await waitForPendingWrites(db);
  return availabilityShareIdFor(ownerId, viewerId);
}

export async function updateAvailabilityShare({
  ownerId,
  viewerId,
  redactMarkTitles,
}) {
  if (!ownerId) throw new Error("ownerId is required.");
  if (!viewerId) throw new Error("viewerId is required.");
  if (typeof redactMarkTitles !== "boolean") {
    throw new Error("redactMarkTitles must be a boolean.");
  }

  await updateDoc(availabilityShareRef(ownerId, viewerId), {
    redactMarkTitles,
    updatedAt: serverTimestamp(),
  });
  await waitForPendingWrites(db);
}

export async function revokeAvailabilityShare({ ownerId, viewerId }) {
  if (!ownerId) throw new Error("ownerId is required.");
  if (!viewerId) throw new Error("viewerId is required.");

  await updateDoc(availabilityShareRef(ownerId, viewerId), {
    status: "revoked",
    updatedAt: serverTimestamp(),
    revokedAt: serverTimestamp(),
  });
  await waitForPendingWrites(db);
}

export async function getAvailabilityShare(ownerId, viewerId) {
  const snap = await getDoc(availabilityShareRef(ownerId, viewerId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function listOutgoingAvailabilityShares(ownerId) {
  const snap = await getDocs(outgoingActiveAvailabilitySharesQuery(ownerId));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function listIncomingAvailabilityShares(viewerId) {
  const snap = await getDocs(incomingActiveAvailabilitySharesQuery(viewerId));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}
